import {defineStore} from "pinia";
import {api} from "boot/axios";
import {IterationStatus, IterationTypes} from "src/logic/iteration";

export const useIterationStore = defineStore('iteration', {
  persist: {
    paths: ['draftIterations', 'pendingIterations']
  },
  state: () => ({
    draftIterations: [], // Drafts are in progress iterations
    pendingIterations: [], // Pending are iterations that have been attempted to be submitted but failed
    iterations: [] // Iterations are completed iterations
  }),
  getters: {
    getDraftIteration: (state) => (iterationGUID) => {
      return state.draftIterations.find((iteration) => iteration.GUID === iterationGUID);
    },
    getDraftIterations: (state) => {
      return state.draftIterations;
    },
    getDraftCaseIterations: (state) => (patientRecordID) => {
      return state.draftIterations.filter((iteration => iteration.patientRecordID === patientRecordID));
    },
    getDraftIterationCount: (state) => {
      return state.draftIterations.length;
    },
    getPendingCaseIterations: (state) => (patientRecordID) => {
      return (state.pendingIterations.filter((iteration => iteration.patientRecordID === patientRecordID)));
    },
    getPendingIteration: (state) => (iterationGUID) => {
      return state.pendingIterations.find((iteration) => iteration.GUID === iterationGUID);
    },
    getPendingIterationCount: (state) => {
      return state.pendingIterations.length;
    },
    getCaseIterations: (state) => (patientRecordID) => {
      return state.iterations.filter((iteration) => iteration.patientRecordID === patientRecordID);
    },
    getIteration: (state) => (iterationGUID) => {
      return state.iterations.find((iteration) => iteration.GUID === iterationGUID);
    }
  },

  actions: {
    saveDraftIterationLocally(newIteration) {
      newIteration.status = IterationStatus.DRAFT;
      if (this.getDraftIteration(newIteration.GUID)) {
        this.updateLocalDraftIteration(newIteration);
      } else {
        this.draftIterations.push(newIteration);
      }
    },
    updateLocalDraftIteration(updatedIteration) {
      updatedIteration.status = IterationStatus.DRAFT;
      this.draftIterations = this.draftIterations.map((iteration) => {
        if (iteration.GUID === updatedIteration.GUID) {
          return updatedIteration;
        }
        return iteration;
      });
    },
    savePendingIterationLocally(newIteration) {
      console.log('savePendingIterationLocally', newIteration)
      newIteration.status = IterationStatus.PENDING;
      if (this.getPendingIteration(newIteration.GUID)) {
        this.updateLocalPendingIteration(newIteration);
      } else {
        this.pendingIterations.push(newIteration);
      }
    },
    updateLocalPendingIteration(updatedIteration) {
      updatedIteration.status = IterationStatus.PENDING;
      this.pendingIterations = this.pendingIterations.map((iteration) => {
        if (iteration.GUID === updatedIteration.GUID) {
          return updatedIteration;
        }
        return iteration;
      });
    },
    saveIterationLocally(newIteration) {
      if (this.getIteration(newIteration.GUID)) {
        this.updateLocalIteration(newIteration);
      } else {
        this.iterations.push(newIteration);
      }
    },
    updateLocalIteration(updatedIteration) {
      this.iterations = this.iterations.map((iteration) => {
        if (iteration.GUID === updatedIteration.GUID) {
          return updatedIteration;
        }
        return iteration;
      });
    },
    deleteLocalIteration(iteration) {
      if (iteration.status === IterationStatus.DRAFT) {
        this.deleteDraftIteration(iteration.GUID);
      } else {
        this.deletePendingIteration(iteration.GUID);
      }
    },
    deleteDraftIteration(iterationGUID) {
      this.draftIterations = this.draftIterations.filter((iteration) => iteration.GUID !== iterationGUID);
    },
    deletePendingIteration(iterationGUID) {
      this.pendingIterations = this.pendingIterations.filter((iteration) => iteration.GUID !== iterationGUID);
    },
    async fetchCaseIterations(patientRecordID) {
      // Validate patientRecordID before making API call
      if (!patientRecordID || patientRecordID <= 0) {
        console.warn('fetchCaseIterations called with invalid patientRecordID:', patientRecordID);
        return;
      }

      try {
        const res = await api.get(`api/v2/cases/${patientRecordID}/iterations`);
        const iterations = res.data
        iterations.forEach((iteration) => this.saveIterationLocally(iteration));
      } catch (err) {
        console.error(err);
      }
    },
    async postIteration(iteration) {
      const clone = JSON.parse(JSON.stringify(iteration));
      try {
        delete clone.status;
        const patientRecordID = clone.patientRecordID;
        const res = await api.post(`api/v2/cases/${patientRecordID}/iterations`, clone)
        const postedIteration = res.data;
        this.saveIterationLocally(postedIteration);
        this.deleteLocalIteration(iteration);
      } catch (err) {
        clone.status = IterationStatus.PENDING;
        if (iteration.status === IterationStatus.DRAFT) {
          this.deleteLocalIteration(iteration);
          this.savePendingIterationLocally(iteration);
        }
      }
    },
    async sync() {
      for (const iteration of this.pendingIterations) {
        await this.postIteration(iteration);
      }
    }
  }
})
